import { formatDate, AsyncPipe } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, inject } from '@angular/core';
import {
  FilterOption,
  NestedFilterOption,
  FiltersConfig,
  FilterTypes,
  FilterResult,
  FilterConfig,
  FilterComponent,
} from '@next/core-lib/components';

// Local type definition - DateRange is not exported from core-lib
interface DateRange {
  description?: string;
  startDate: Date;
  endDate: Date;
}

import { LocaleService } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subject, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map, skipWhile, take, takeUntil } from 'rxjs/operators';

import {
  DataViewType,
  FarmerInsightsModuleState,
  FlockDataParameter,
  QueryableFlockData,
} from '../../models/farmer-insights.model';
import { ViewMode } from '../../models/farmer-insights.model';
import {
  DateRange as DateRangeModel,
  DefaultKPIselection,
  KpiGroup,
  KpiGroupingChild,
} from './../../models/farmer-insights.model';
import {
  setDataViewTypeFilter,
  setDetailedView,
  setSelectedConceptId,
  setSelectedViewMode,
  setDateRangeFilter,
  setKpisFilter,
  setSlideInFilters,
  setDataFilters,
} from '../../store/actions/farmer-insights.actions';
import {
  getIsDetailedModeSelected,
  getKpiGroups,
  getQueryableFlockData,
  getSelectedBarnIds,
  getSelectedConceptId,
  getSelectedLocations,
  getUnselectedCycleIds,
  getSelectedDataViewType,
  getSelectedDateRange,
  getSelectedKpis,
  getSelectedViewMode,
} from '../../store/selectors/farmer-insights.selectors';

@Component({
  selector: 'app-data-filter',
  templateUrl: './data-filter.component.html',
  imports: [FilterComponent, AsyncPipe],
})
export class DataFilterComponent implements AfterViewInit, OnDestroy {
  private readonly store = inject<Store<FarmerInsightsModuleState>>(Store);
  private readonly locale = inject(LocaleService);

  private readonly destroyed$ = new Subject<void>();

  // filter changes
  private readonly departureDateChangeSubject$: Subject<DateRange> = new Subject<DateRange>();
  private readonly conceptChangeSubject$: Subject<number> = new Subject<number>();
  private readonly cyclesChangeSubject$: Subject<number[]> = new ReplaySubject<number[]>(1);
  private readonly barnsChangeSubject$: Subject<number[]> = new ReplaySubject<number[]>(1);

  // settings changes
  private readonly dataViewChangeSubject$: Subject<DataViewType> = new Subject<DataViewType>();
  private readonly viewModeChangeSubject$: Subject<ViewMode> = new Subject<ViewMode>();
  private readonly detailedViewChangeSubject$: Subject<boolean> = new Subject<boolean>();

  // Filter values
  private readonly selectedDateRange$: Observable<DateRangeModel> =
    this.store.select(getSelectedDateRange);
  private readonly selectedConceptId$: Observable<number> = this.store.select(getSelectedConceptId);
  private readonly queryableFlockData$: Observable<QueryableFlockData> =
    this.store.select(getQueryableFlockData);
  private readonly kpiGroups$: Observable<KpiGroup[]> = this.store.select(getKpiGroups);
  private readonly selectedBarnIds$: Observable<number[]> = this.store.select(getSelectedBarnIds);
  private readonly selectedCycleIds$: Observable<number[]> = combineLatest([
    this.store.select(getUnselectedCycleIds),
    this.queryableFlockData$,
    this.selectedDateRange$,
    this.selectedConceptId$,
  ]).pipe(
    skipWhile(
      ([unselectedCycleIds, flockData, _, conceptId]) =>
        !unselectedCycleIds || unselectedCycleIds.length === 0 || !flockData || conceptId === null,
    ),
    takeUntil(this.destroyed$),
    distinctUntilChanged(),
    map(
      // the not-selected cycles are actually saved in the settingservice,
      // so the selection must be inverted to get the really selected ids.
      ([notSelectedCycleIds, flockData, dateRange, selectedConceptId]) => <number[]>flockData.cycles
          .filter(
            (cycle) =>
              new Date(cycle.cycleEndDate).getTime() >= new Date(dateRange.startDate).getTime() &&
              new Date(cycle.cycleEndDate).getTime() < new Date(dateRange.endDate).getTime() &&
              cycle.conceptId === selectedConceptId,
          )
          .map((cycle) => cycle.cycleId)
          .filter((cycleId) => !(<number[]>notSelectedCycleIds).includes(cycleId)),
    ),
  );

  // Settings values
  private readonly selectedDataViewType$: Observable<DataViewType> =
    this.store.select(getSelectedDataViewType);
  private readonly selectedViewMode$: Observable<ViewMode> = this.store.select(getSelectedViewMode);
  private readonly isDetailedView$: Observable<boolean> =
    this.store.select(getIsDetailedModeSelected);
  private readonly selectedKpis$: Observable<FlockDataParameter[]> =
    this.store.select(getSelectedKpis);

  private queryableFlockData: QueryableFlockData = undefined;
  private sortedKpis: KpiGroupingChild[] = undefined;

  // Filter options
  private readonly kpiFilterOptions$: Observable<NestedFilterOption<FlockDataParameter>[]> =
    this.kpiGroups$.pipe(
      map((kpiGroups) => {
        const kpiGroupFilterOptions: NestedFilterOption<FlockDataParameter>[] = [];
        kpiGroups.forEach((kpiGroup: KpiGroup) => {
          if (kpiGroup.name === '' && kpiGroup.children.length === 1) {
            kpiGroupFilterOptions.push({
              value: kpiGroup.children[0].kpi,
              viewValue: this.locale.translate(`farmer-insights.kpis.${kpiGroup.children[0].kpi}`),
              children: [],
              expand: false,
            });
          } else {
            kpiGroupFilterOptions.push({
              viewValue: this.locale.translate(`farmer-insights.kpiGroups.${kpiGroup.name}`),
              children: kpiGroup.children.map((c) => ({
                value: c.kpi,
                viewValue: this.locale.translate(`farmer-insights.kpis.${c.kpi}`),
              })),
              expand: true,
            });
          }
        });
        return kpiGroupFilterOptions;
      }),
    );

  private readonly cycleFilterOptions$: Observable<NestedFilterOption<number>[]> = combineLatest([
    this.queryableFlockData$,
    this.selectedDateRange$,
    this.selectedConceptId$,
  ]).pipe(
    map(([queryableFlockData, dateRange, selectedConceptId]) => {
      if (!queryableFlockData) {
        return [];
      }

      return queryableFlockData.locations.map(({ locationName, locationId }) => ({
        viewValue: locationName,
        expand: false,
        children: queryableFlockData.cycles
          .filter(
            (cycle) =>
              cycle.locationId === locationId &&
              new Date(cycle.cycleEndDate).getTime() >= new Date(dateRange.startDate).getTime() &&
              new Date(cycle.cycleEndDate).getTime() < new Date(dateRange.endDate).getTime() &&
              cycle.conceptId === selectedConceptId,
          )
          .map(({ cycleId, cycleNumber }) => ({
            value: cycleId,
            viewValue: `${this.locale.translate('cycle')}: ${cycleNumber}`,
          }))
          .sort((a, b) => (a.viewValue > b.viewValue ? 1 : b.viewValue > a.viewValue ? -1 : 0)),
      }));
    }),
  );

  private readonly barnFilterOption$: Observable<NestedFilterOption<number>[]> = combineLatest([
    this.cyclesChangeSubject$,
    this.queryableFlockData$,
  ]).pipe(
    map(([cyclesUsed, queryableFlockData]) => {
      if (!queryableFlockData || !cyclesUsed) {
        return [];
      }

      return queryableFlockData.locations
        .map(({ locationName, locationId }) => ({
          viewValue: locationName,
          expand: false,
          children: queryableFlockData.cycles
            .filter(
              (cycle) =>
                cycle.locationId === locationId && cyclesUsed.indexOf(cycle.cycleId) !== -1,
            )
            .reduce(
              (acc, { barnIds }) => [
                ...barnIds.filter((barnId) => acc.indexOf(barnId) === -1),
                ...acc,
              ],
              [],
            )
            .map((barnId) => queryableFlockData.barns.find((barn) => barn.barnId === barnId))
            .map(({ barnName, barnId }) => ({ value: barnId, viewValue: barnName }))
            .sort((a, b) => (a.viewValue > b.viewValue ? 1 : b.viewValue > a.viewValue ? -1 : 0)),
        }))
        .filter(({ children }) => children.length);
    }),
  );

  // filter options
  private readonly conceptFilterOptions$: Observable<FilterOption<number>[]> =
    this.queryableFlockData$.pipe(
      map((queryableFlockData) => {
        if (!queryableFlockData) {
          return [];
        }
        return queryableFlockData.concepts.map((concept) => ({
          viewValue: concept.name,
          value: concept.conceptId,
        }));
      }),
    );

  // settings options
  private readonly dataViewTypeListItems$: Observable<FilterOption<string>[]> = of(
    Object.keys(DataViewType).map((dataViewType) => ({
      viewValue: this.locale.translate(`farmer-insights.data-view-types.${dataViewType}`),
      value: dataViewType,
    })),
  );

  infotext$: Observable<string> = combineLatest([
    this.conceptChangeSubject$,
    this.conceptFilterOptions$,
    this.departureDateChangeSubject$,
  ]).pipe(
    skipWhile(
      ([selectedConcept, concepts, __]) => selectedConcept === null || concepts.length === 0,
    ),
    takeUntil(this.destroyed$),
    distinctUntilChanged(),
    map(([selectedConcept, concepts, dateRange]) => {
      const dates =
        dateRange !== null
          ? `${formatDate(dateRange.startDate, 'd MMM yy', this.locale.getLocale())} - ${formatDate(
              dateRange.endDate,
              'd MMM yy',
              this.locale.getLocale(),
            )}`
          : '';
      const foundConcept = concepts.find((c) => c.value === selectedConcept);
      let concept = '';
      if (foundConcept) {
        concept = `${this.locale.translate('concept')}: <strong>${
          foundConcept.viewValue
        }</strong>, `;
      }
      return `${concept}${this.locale.translate('departure-date')}: <strong>${dates}</strong>`;
    }),
  );

  filterConfig: FilterConfig<FiltersConfig> = {
    altButton: true,
    altButtonText: this.locale.translate('farmer-insights.reset-filters'),
    filters: {
      departureDate: {
        type: FilterTypes.Daterange,
        label: this.locale.translate('cycle-overview.panel.departuredate'),
        value: this.selectedDateRange$.pipe(
          map((dateRange) =>
            dateRange !== null
              ? {
                  startDate: new Date(dateRange.startDate),
                  endDate: new Date(dateRange.endDate),
                  description: dateRange.description,
                }
              : {
                  startDate: undefined,
                  endDate: undefined,
                  description: undefined,
                },
          ),
        ),
        changes: this.departureDateChangeSubject$,
        priority: true,
      },
      concept: {
        type: FilterTypes.Select,
        label: this.locale.translate('concept'),
        options: this.conceptFilterOptions$,
        value: this.selectedConceptId$,
        changes: this.conceptChangeSubject$,
        priority: true,
      },
      cycles: {
        type: FilterTypes.Tree,
        label: this.locale.translate('farmer-insights.cycles'),
        options: this.cycleFilterOptions$,
        value: this.selectedCycleIds$,
        changes: this.cyclesChangeSubject$,
        priority: false,
      },
      barns: {
        type: FilterTypes.Tree,
        label: this.locale.translate('farmer-insights.barns'),
        options: this.barnFilterOption$,
        value: this.selectedBarnIds$,
        changes: this.barnsChangeSubject$,
        priority: false,
      },
      kpis: {
        type: FilterTypes.Tree,
        label: this.locale.translate('farmer-insights.kpis-name'),
        value: this.selectedKpis$,
        options: this.kpiFilterOptions$,
        priority: false,
      },
    },
  };

  settingsConfig: FilterConfig<FiltersConfig> = {
    settings: true,
    filters: {
      dataView: {
        type: FilterTypes.Radio,
        label: this.locale.translate('farmer-insights.detail-level'),
        value: this.selectedDataViewType$,
        options: this.dataViewTypeListItems$,
        changes: this.dataViewChangeSubject$,
        priority: true,
      },
      viewMode: {
        type: FilterTypes.Radio,
        label: this.locale.translate('farmer-insights.display-type'),
        value: this.selectedViewMode$,
        options: of([
          {
            value: 'Table',
            viewValue: this.locale.translate('farmer-insights.table'),
          },
          {
            value: 'Chart',
            viewValue: this.locale.translate('farmer-insights.chart'),
          },
        ]),
        priority: true,
        changes: this.viewModeChangeSubject$,
      },
      detailedView: {
        type: FilterTypes.Toggle,
        label: this.locale.translate('cycle-overview.detailedOverview'),
        value: this.isDetailedView$,
        changes: this.detailedViewChangeSubject$,
        priority: true,
      },
    },
  };

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngAfterViewInit() {
    this.departureDateChangeSubject$
      .pipe(
        takeUntil(this.destroyed$),
        skipWhile((dateRange) => !dateRange.startDate || !dateRange.endDate),
        distinctUntilChanged(
          (a, b) =>
            a.startDate.getTime() === b.startDate.getTime() &&
            a.endDate.getTime() === b.endDate.getTime(),
        ),
      )
      .subscribe((dateRange) => {
        this.store.dispatch(
          setDateRangeFilter({
            startDate: dateRange.startDate.toISOString(),
            endDate: dateRange.endDate.toISOString(),
            description: dateRange.description,
          }),
        );
      });
    this.conceptChangeSubject$
      .pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe((conceptId) => this.store.dispatch(setSelectedConceptId({ conceptId })));
    this.dataViewChangeSubject$
      .pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe((dataViewType) => this.store.dispatch(setDataViewTypeFilter({ dataViewType })));
    this.viewModeChangeSubject$
      .pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe((viewMode) => this.store.dispatch(setSelectedViewMode({ viewMode })));
    this.detailedViewChangeSubject$
      .pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe((isDetailedView) => this.store.dispatch(setDetailedView({ isDetailedView })));

    this.queryableFlockData$
      .pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe((queryableFlockData) => (this.queryableFlockData = queryableFlockData));

    this.kpiGroups$
      .pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe((kpiGroups) => {
        this.sortedKpis = kpiGroups
          .map((d) => d.children)
          .reduce((acc, val) => acc.concat(val), [])
          .sort((a, b) => (a.order > b.order ? 1 : b.order > a.order ? -1 : 0));
      });
  }

  resetFilters() {
    if (this.queryableFlockData) {
      this.store.dispatch(
        setSlideInFilters({
          locationIds: <number[]>this.queryableFlockData.locations.map((l) => l.locationId),
          cycleIds: <number[]>this.queryableFlockData.cycles.map((c) => c.cycleId),
          barnIds: <number[]>this.queryableFlockData.barns.map((b) => b.barnId),
        }),
      );
    }
  }

  restoreKpiSelection() {
    if (this.queryableFlockData) {
      const filteredKpis: FlockDataParameter[] = <FlockDataParameter[]>DefaultKPIselection;
      this.store.dispatch(
        setKpisFilter({
          KPIs: this.sortedKpis.filter((d) => filteredKpis.indexOf(d.kpi) >= 0).map((d) => d.kpi),
        }),
      );
    }
  }

  filterDialogSaved(filters: FilterResult<FiltersConfig>) {
    if (
      !this.queryableFlockData ||
      !this.sortedKpis ||
      !Array.isArray(filters.cycles) ||
      !Array.isArray(filters.barns) ||
      !Array.isArray(filters.kpis)
    ) {
      return;
    }

    const selectedCycleIds = <number[]>filters.cycles;
    const filteredKpis = <FlockDataParameter[]>filters.kpis;
    const unselectedCycleIds = this.queryableFlockData.cycles
      .map((cycle) => cycle.cycleId)
      .filter((cycleId) => !selectedCycleIds.includes(cycleId));

    this.store
      .select(getSelectedLocations)
      .pipe(take(1))
      .subscribe((selectedLocationIds) => {
        this.store.dispatch(
          setDataFilters({
            locationIds: Array.isArray(selectedLocationIds)
              ? selectedLocationIds
              : this.queryableFlockData.locations.map((location) => location.locationId),
            cycleIds: unselectedCycleIds,
            barnIds: <number[]>filters.barns,
            KPIs: this.sortedKpis
              .filter((kpi) => filteredKpis.includes(kpi.kpi))
              .map((kpi) => kpi.kpi),
          }),
        );
      });
  }
}
